
import Vue from 'vue';
import gql from 'graphql-tag';
import Multiselect from 'vue-multiselect';
import { hierarchySort } from '~shared/utils';

interface IOption {
  id: string;
  name: string;
}

interface IData {
  options: IOption[];
  query: string;
  initialFetched: boolean;
  skipQuery: boolean;
}

export default Vue.extend({
  components: {
    Multiselect,
  },
  props: {
    group: {
      type: Object,
      required: false,
    },
    enableQueryParam: {
      type: Boolean,
      default: true,
    },
    enableAutoFillFirst: {
      type: Boolean,
      default: true,
    },
    enableAutoFillSingle: {
      type: Boolean,
      default: true,
    },
    enableDisplayParent: {
      type: Boolean,
      default: false,
    },
    allOptionsText: {
      type: String,
      default: '',
    },
    reloadOnClearingGroupParam: {
      type: Boolean,
      default: false,
    },
  },
  data(): IData {
    return {
      options: [],
      query: '',
      initialFetched: false,
      skipQuery: true,
    };
  },
  watch: {
    '$route.query': {
      handler(val, oldVal) {
        if (this.reloadOnClearingGroupParam && oldVal.group && !val.group) {
          location.reload();
          return;
        }
        if (this.group && val.group && val.group !== this.group.id) {
          const groupId = val.group ? val.group : '';
          const find = this.options.find(o => o.id === groupId);
          if (find) {
            this.setGroup(find);
          } else {
            this.setGroup({ id: '', name: this.allOptionsText });
          }
        }
      },
    },
  },
  async mounted(): Promise<void> {
    const isGroupPropValid = group =>
      group && group.id && group.name && group.meta;

    // Fetch group if we don't have a group sent in with all fields
    if (
      !isGroupPropValid(this.group) &&
      (this.$route.query.group || (this.group && this.group.id))
    ) {
      // Group id present in the query, fetch group and set it
      let groupIdToFetch;
      if (this.enableQueryParam && this.$route.query.group) {
        groupIdToFetch = this.$route.query.group;
      } else if (this.group.id) {
        groupIdToFetch = this.group.id;
      }
      try {
        this.initialFetched = true;
        const result = await this.$apollo.query({
          query: gql`
            query($id: ID!) {
              Group(id: $id) {
                id
                name
                language
                meta {
                  parents {
                    id
                    name
                    parentId
                  }
                }
              }
            }
          `,
          variables: {
            id: groupIdToFetch,
          },
        });
        if (result.data && result.data.Group) {
          this.setGroup(result.data.Group);
        }
      } catch (e: any) {
        if (e.message === 'GraphQL error: Permission denied.') {
          this.$snotify.error('You do not have access to this publisher.');
        } else {
          this.$snotify.error(e.message);
        }
      }
    }
    this.skipQuery = false;
  },
  methods: {
    async setGroup(v): Promise<void> {
      if (v && v.id !== this.$route.query.group && this.enableQueryParam) {
        try {
          await this.$router.push({
            query: Object.assign({}, this.$route.query, {
              // undefined is removing the query param
              group: v.id ? v.id : undefined,
            }),
          });
        } catch (err: any) {
          if (typeof err === 'object' && err.name !== 'NavigationDuplicated') {
            throw err;
          }
        }
      }
      this.$emit('input', v);
    },
    search(q: string): void {
      this.query = q;
    },
    hierarchySort,
  },
  apollo: {
    options: {
      query: gql`
        query($query: String!) {
          Groups(query: $query, limit: 20) {
            groups {
              id
              name
              language
              isAdvertiserMigrated
              meta {
                parents {
                  id
                  name
                  parentId
                }
              }
            }
          }
        }
      `,
      variables(): { query: string } {
        return {
          query: this.query,
        };
      },
      update(data): any {
        if (
          data &&
          data.Groups &&
          data.Groups.groups &&
          data.Groups.groups.length
        ) {
          let currentGroup = null;

          // enableAutoFillFirst on initial fetch
          if (!this.initialFetched && this.enableAutoFillFirst) {
            currentGroup = data.Groups.groups[0];
            this.setGroup(currentGroup);
          }

          // enableAutoFillSingle on initial fetch
          if (
            !this.initialFetched &&
            data.Groups.groups.length === 1 &&
            this.enableAutoFillSingle
          ) {
            currentGroup = data.Groups.groups[0];
            this.setGroup(currentGroup);
          }

          const groups = data.Groups.groups.slice(0);

          const all = { name: this.allOptionsText, id: '' };
          // allOptionsText on initial fetch, set if no group has been selected
          if (!this.initialFetched && all.name && !currentGroup) {
            this.setGroup(all);
          }
          // Add allOptionsText first in the list if enabled
          if (all.name) {
            groups.unshift(all);
          }

          this.initialFetched = true;
          return groups;
        }
        return [];
      },
      skip(): boolean {
        return this.skipQuery;
      },
      fetchPolicy: 'network-only',
      debounce: 500,
    },
  },
});
